import { Navigate, Outlet, createFileRoute, useMatch } from '@tanstack/react-router'

import { useLocalStore } from '~/stores'
import { userPreferencesQueryOptions } from '~/queries/user'
import { setAccountPreference } from '~/services/preferences/userPreferences'
import { queryClient } from '~/providers/ReactQuery'
import { setTag } from '@sentry/react'
import { getQueryKey, useSuspenseQuery } from '~/services'
import { resolveKey } from '~/services/preferences/preferences'
import { getUseBotOnboardingStore } from '~/features/bots/stores/useBotOnboardingStore'
import { useGetWorkspacePreferences } from '~/hooks/users/useGetWorkspacePreferences'

export const Route = createFileRoute('/workspaces/$workspaceId/bots/$botId')({
  loader: async ({ context, params }) => {
    const { setLastActiveWorkspaceAndBotIds } = useLocalStore.getState()
    setLastActiveWorkspaceAndBotIds(params.workspaceId, params.botId)
    const preferenceProps = { path: '$workspaceId/botHistory', params: { workspaceId: params.workspaceId } } as const
    const botHistory = await context.queryClient.ensureQueryData(userPreferencesQueryOptions(preferenceProps))
    const newHistory = [params.botId, ...botHistory].slice(0, 50)
    void setAccountPreference({ ...preferenceProps, value: newHistory }).catch(() => {})

    const pathKey = resolveKey(preferenceProps)
    const queryKey = getQueryKey('preferences/$path', { path: pathKey })

    queryClient.setQueryData(queryKey, () => newHistory)
    setTag('botId', params.botId)

    const useBotOnboardingStore = getUseBotOnboardingStore({ botId: params.botId, workspaceId: params.workspaceId })
    const onboardingStep = useBotOnboardingStore.getState().deploymentProgress.currentStep
    return { onboardingStep }
  },
  component: BotRouteComponent,
})

function BotRouteComponent() {
  const { botId, workspaceId } = Route.useParams()
  const { data: bot } = useSuspenseQuery('workspaces_/$workspaceId_/bots_/$botId_', { botId, workspaceId })
  const { data: isBotOnboarding } = useGetWorkspacePreferences(
    { path: 'isOnboarding/$botId', params: { botId } },
    workspaceId
  )

  const { onboardingStep } = Route.useLoaderData()
  const newBotDeployComplete = onboardingStep === 'completed'
  const newBotDeployAck = onboardingStep === 'acknowledged'

  const onNewBot = !!useMatch({
    from: '/workspaces/$workspaceId/bots/$botId/new-bot',
    shouldThrow: false,
  })

  const onNewChat = !!useMatch({
    from: '/workspaces/$workspaceId/bots/$botId/new-chat',
    shouldThrow: false,
  })

  if (!isBotOnboarding || bot.dev || (isBotOnboarding && onNewBot)) {
    return <Outlet />
  }

  if ((newBotDeployComplete || newBotDeployAck) && onNewChat) {
    return <Outlet />
  }

  return <Navigate params={{ workspaceId, botId }} to="/workspaces/$workspaceId/bots/$botId/new-bot" replace />
}
